import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { apiError, fmtUSD } from "../lib/api";

function DealFacts({ info }) {
  return (
    <div className="space-y-2">
      {info.sector && (
        <div className="flex justify-between border-t border-[#24272B] pt-2">
          <span className="t-cap text-muted-s">Sector</span><span className="text-bone t-small">{info.sector}</span>
        </div>
      )}
      {info.minimum_usd != null && (
        <div className="flex justify-between border-t border-[#24272B] pt-2">
          <span className="t-cap text-muted-s">Minimum</span><span className="text-bone t-small fig">{fmtUSD(info.minimum_usd)}</span>
        </div>
      )}
      {info.closes_at && (
        <div className="flex justify-between border-t border-[#24272B] pt-2">
          <span className="t-cap text-muted-s">Closes</span><span className="text-bone t-small">{info.closes_at}</span>
        </div>
      )}
    </div>
  );
}

function ResponseClosed() {
  return (
    <div className="border border-[#24272B] px-5 py-6 text-center" data-testid="deal-response-closed">
      <p className="text-bone text-base">This allocation has closed.</p>
      <p className="text-muted-s t-small mt-2">Responses are no longer being taken. Sign in to see your current deal flow.</p>
      <Link to="/login" className="btn-primary inline-block mt-6" data-testid="deal-response-signin">Open Solera</Link>
    </div>
  );
}

function ResponseConfirmed({ info, busy, submit, memoPath }) {
  const committed = info.response === "commit";
  const interested = info.response === "interested";
  const positive = committed || interested;
  return (
    <div className="border border-[#24272B] px-5 py-6 text-center" data-testid="deal-response-confirmed">
      <p className="text-bone text-base">
        {committed
          ? "Committed. We have your firm intent on record."
          : interested
            ? "Noted. We have your interest on record."
            : "Understood. You have passed on this one."}
      </p>
      {positive && info.position && (
        <p className="text-bone t-small mt-2" data-testid="deal-response-position">You are number {info.position} of {info.total_interested} interested.</p>
      )}
      <p className="text-muted-s t-small mt-2">
        {committed
          ? "Confirm your amount and sign the memo inside Solera to finalise. Nothing is binding until you sign, and allocations follow in the order members responded."
          : interested
            ? "Review the memo and commit inside Solera. Nothing is committed until you sign, and interested members are allocated in the order they responded."
            : "You can change your mind while the deal is open."}
      </p>
      <div className="flex gap-3 justify-center mt-6 flex-wrap">
        {positive ? (
          <>
            <Link to={memoPath} className={committed ? "btn-commit" : "btn-primary"} data-testid="deal-response-commit">
              {committed ? "Confirm your commitment" : "Review the memo and commit"}
            </Link>
            <button className="btn-ghost" disabled={busy} onClick={() => submit("pass")} data-testid="deal-response-change-pass">Actually, pass</button>
          </>
        ) : (
          <>
            <button className="btn-primary" disabled={busy} onClick={() => submit("interested")} data-testid="deal-response-change-interested">I am interested</button>
            <Link to="/login" className="btn-ghost" data-testid="deal-response-signin">Open Solera</Link>
          </>
        )}
      </div>
    </div>
  );
}

function ResponseChoice({ busy, submit }) {
  return (
    <div className="space-y-3">
      <p className="text-muted-s t-small">Tell us where you stand. One tap.</p>
      <div className="flex gap-3">
        <button className="btn-primary flex-1" disabled={busy} onClick={() => submit("interested")} data-testid="deal-response-interested">
          {busy ? "Saving" : "I am interested"}
        </button>
        <button className="btn-ghost flex-1" disabled={busy} onClick={() => submit("pass")} data-testid="deal-response-pass">
          Pass this one
        </button>
      </div>
      <button className="btn-commit w-full" disabled={busy} onClick={() => submit("commit")} data-testid="deal-response-commit-now">
        {busy ? "Saving" : "Commit now"}
      </button>
      <p className="text-muted-s t-cap">Commit records your firm intent and moves you to the front of the queue. Nothing is binding until you confirm your amount and sign the memo inside Solera.</p>
    </div>
  );
}

function ResponseBody({ info, busy, submit }) {
  const closed = info.status && info.status !== "open";
  const memoPath = info.deal_id ? `/app/deals/${info.deal_id}` : "/app";
  const answered = info.response && info.response !== "pending";
  return (
    <div className="px-7 py-7 space-y-6">
      <div>
        <h1 className="text-bone" style={{ fontFamily: "Georgia, serif", fontSize: 26, lineHeight: 1.15 }} data-testid="deal-response-company">
          {info.company || "This allocation"}
        </h1>
        {info.round_label && <p className="text-muted-s t-small mt-1">{info.round_label}</p>}
      </div>
      <DealFacts info={info} />
      {closed ? (
        <ResponseClosed />
      ) : answered ? (
        <ResponseConfirmed info={info} busy={busy} submit={submit} memoPath={memoPath} />
      ) : (
        <ResponseChoice busy={busy} submit={submit} />
      )}
    </div>
  );
}

export default function DealResponse() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/deal-response/${token}`, { action });
      setInfo((s) => ({ ...(s || {}), response: data.response, company: data.company, deal_id: data.deal_id, position: data.position, total_interested: data.total_interested }));
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api
      .get(`/public/deal-response/${token}`)
      .then((r) => {
        setInfo(r.data);
        const a = params.get("a");
        if (r.data.status === "open" && (a === "interested" || a === "pass" || a === "commit")) submit(a);
      })
      .catch((e) => setError(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const closed = info && info.status && info.status !== "open";

  return (
    <div className="min-h-screen bg-[#0E1012] flex flex-col items-center justify-center px-5 py-16" data-testid="deal-response-page">
      <Link to="/" className="mb-10 block">
        <span className="font-arial text-[#E8E6E1]" style={{ letterSpacing: "3px", textTransform: "uppercase", fontSize: 15 }}>Solera</span>
      </Link>

      <div className="w-full max-w-md border border-[#24272B] bg-[#121417]">
        <div className="px-7 py-6 border-b border-[#24272B]">
          <span className="t-label">{closed ? "Allocation closed" : "New allocation"}</span>
        </div>

        {error && (
          <div className="px-7 py-10 text-center" data-testid="deal-response-error">
            <p className="text-bone text-base">{error}</p>
            <Link to="/login" className="btn-ghost inline-block mt-6">Go to sign in</Link>
          </div>
        )}

        {!error && !info && (
          <div className="px-7 py-12 text-center">
            <span className="t-label">Loading</span>
          </div>
        )}

        {!error && info && <ResponseBody info={info} busy={busy} submit={submit} />}
      </div>

      <p className="text-muted-s t-cap mt-8 max-w-md text-center">
        Confidential and for members only. Solera will never ask you to send funds or documents by email.
      </p>
    </div>
  );
}
